import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

// Cache admin status to avoid repeated API calls
let adminCache: { userId: string; isAdmin: boolean; timestamp: number } | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useAdmin() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      // Clear cache if user changed
      if (adminCache && adminCache.userId !== user.id) {
        adminCache = null;
      }

      // Check cache first
      if (adminCache && 
          adminCache.userId === user.id && 
          Date.now() - adminCache.timestamp < CACHE_DURATION) {
        if (import.meta.env.DEV) console.log('🔐 useAdmin: Using cached admin status:', adminCache.isAdmin);
        setIsAdmin(adminCache.isAdmin);
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('get_current_user_role');
        
        const userIsAdmin = data === 'admin' || data === 'staff';
        
        if (error) {
          if (import.meta.env.DEV) console.warn('🔐 useAdmin: RPC failed, using fallback query:', error);
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('role')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (profileError) throw profileError;
          const fallbackIsAdmin = profile?.role === 'admin' || profile?.role === 'staff';
          
          setIsAdmin(fallbackIsAdmin);
          adminCache = {
            userId: user.id,
            isAdmin: fallbackIsAdmin,
            timestamp: Date.now()
          };
        } else {
          setIsAdmin(userIsAdmin);
          adminCache = {
            userId: user.id,
            isAdmin: userIsAdmin,
            timestamp: Date.now()
          };
        }
      } catch (e) {
        console.error('🔐 useAdmin: Error checking admin status:', e);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  return { isAdmin, loading, user };
}
